import { useState } from 'react';
import RenderPostUrlIcon from './RenderPostUrlIcon/RenderPostUrlIcon';
import { isValidTikTokPostUrl, sanitizeTikTokPostUrl } from 'utility/tiktokPostValidation';
import { microwave } from '@round/api';
import { isValidInstagramPostUrl, sanitizeInstagramPostUrl } from 'utility/instagramPostValidation';

export function useValidatePostUrl(uuid: string, platform: 'tiktok' | 'instagram') {
    const [postUrlValidationMessage, setPostUrlValidationMessage] = useState<string | null>(null);
    const [postUrlInputStatus, setPostUrlInputStatus] =
        useState<Parameters<typeof RenderPostUrlIcon>[0]['status']>('PENDING');

    const handleLocalValidationFailure = (message: string) => {
        setPostUrlInputStatus('INVALID');
        setPostUrlValidationMessage(message);
    };

    const validateInstagramUrl = (value: string) => {
        const sanitizedValue = sanitizeInstagramPostUrl(value);
        if (isValidInstagramPostUrl(sanitizedValue)) {
            setPostUrlInputStatus('VALID');
            return;
        }
        handleLocalValidationFailure('Invalid Instagram Post URL');
    };

    const validateTikTokUrl = async (value: string) => {
        const sanitizedValue = sanitizeTikTokPostUrl(value);
        const localValidationResult = isValidTikTokPostUrl(sanitizedValue);
        if (!localValidationResult.isValid) {
            handleLocalValidationFailure(localValidationResult.message);
            return;
        }

        try {
            setPostUrlInputStatus('LOADING');
            const response = await microwave.validateTiktokPostUrl(uuid, value);
            if (response.status === 400) {
                setPostUrlInputStatus('INVALID');
                setPostUrlValidationMessage(response.data.post_url?.[0] || 'Invalid TikTok Post URL');
                return;
            }

            setPostUrlInputStatus('VALID');
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            setPostUrlValidationMessage(`Couldn't validate the url. Please try again later.`);
            setPostUrlInputStatus('INVALID');
        }
    };

    const handleValidatePostUrl = async (value: string) => {
        if (postUrlInputStatus !== 'PENDING') {
            return;
        }

        const validate = platform === 'tiktok' ? validateTikTokUrl : validateInstagramUrl;

        validate(value);
    };

    return {
        postUrlValidationMessage,
        postUrlInputStatus,
        handleValidatePostUrl,
        resetPostUrlValidationState: () => {
            setPostUrlValidationMessage(null);
            setPostUrlInputStatus('PENDING');
        },
    };
}
